<template>
  <section v-if="isCoveredCategory" ref="sectionRef">
    <header class="mb-24 md:mb-32 md:text-center">
      <h3 class="heading-3 mb-12">{{ i18n(translations.productFeature) }}</h3>
      <h4 class="punchline-sans">
        {{ i18n(translations.aboutBatteryHealth) }}
      </h4>
    </header>

    <div class="grid grid-cols-1 gap-16 md:grid-cols-4 md:gap-20">
      <RevIllustration
        alt=""
        class="rounded-[20px] md:col-span-2 md:row-span-2"
        :height="800"
        role="presentation"
        :src="illustration"
        :width="800"
      />

      <ContentTile
        class="bg-static-info-mid md:col-span-2"
        :heading="i18n(translations.capacityGuaranteedHeading)"
        :icon="IconBattery"
      >
        <p class="body-2">
          {{ i18n(translations.capacityGuaranteedContent) }}
          <RevLink :onClick="onLearnMoreClick">{{
            i18n(translations.learnMore)
          }}</RevLink>
        </p>
      </ContentTile>

      <ContentTile
        class="bg-static-warning-mid"
        :heading="i18n(translations.newBatteryHeading)"
        :icon="IconSparkles"
      >
        <p class="body-2">{{ i18n(translations.newBatteryContent) }}</p>
      </ContentTile>

      <ContentTile
        class="bg-static-success-mid"
        :heading="i18n(translations.testedVerifiedHeading)"
        :icon="IconCheckInCircle"
      >
        <p class="body-2">{{ i18n(translations.testedVerifiedContent) }}</p>
      </ContentTile>
    </div>

    <AboutBatteryModal :illustration :is-smartphone-category />
  </section>
</template>

<script lang="ts" setup>
import { computed, onUnmounted, ref } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevIllustration } from '@ds/components/Illustration'
import { RevLink } from '@ds/components/Link'
import { openModal } from '@ds/components/ModalBase'
import { IconBattery } from '@ds/icons/IconBattery'
import { IconCheckInCircle } from '@ds/icons/IconCheckInCircle'
import { IconSparkles } from '@ds/icons/IconSparkles'
import { useIntersectionObserver } from '@vueuse/core'

import { ProductCategory } from '~/scopes/product/constants'

import { MODAL_NAMES } from '../../Product-JP.constants'
import AboutBatteryModal from '../AboutBatteryModal/AboutBatteryModal.vue'

import translations from './AboutBatteryBlock.translations'
import ContentTile from './components/ContentTile.vue'

const { categoryId } = defineProps<{
  categoryId: string
}>()

const i18n = useI18n()

const { trackProductBlockImpression } = useTracking()

const sectionRef = ref<HTMLElement | null>()

const isSmartphoneCategory = computed(
  () => ProductCategory.SMARTPHONE === categoryId,
)

const isMacbookCategory = computed(
  () =>
    ProductCategory.MACBOOK_M === categoryId ||
    ProductCategory.MACBOOK_INTEL === categoryId,
)

const isCoveredCategory = computed(
  () => isSmartphoneCategory.value || isMacbookCategory.value,
)

const illustration = computed(() => {
  if (isSmartphoneCategory.value) {
    return '/img/product/jp/smartphone_battery.jpg'
  }

  if (isMacbookCategory.value) {
    return '/img/product/jp/laptop_battery.jpg'
  }

  return ''
})

function onLearnMoreClick() {
  openModal(MODAL_NAMES.ABOUT_BATTERY)
}

const { stop } = useIntersectionObserver(sectionRef, ([{ isIntersecting }]) => {
  if (isIntersecting) {
    trackProductBlockImpression({
      block_name: 'battery_block',
    })
  }
})

onUnmounted(() => {
  stop()
})
</script>
